import { selectRow } from '@client-shared/ui-agGrid';
import _keyBy from 'lodash/keyBy';

import { fetchingError } from '../../shared/store/actions';
import { errorNotification, successNotification } from '../../shared/store/notificationActions';
import { assignablePermissionTypes } from '@common/constants/assignablePermissions';

import {
  fetchUsers as fetchUsersApi,
  fetchUserSettingsFormData as fetchUserSettingsFormDataApi,
  fetchUser as fetchUserApi,
  addUserSettings as addUserSettingsApi,
  updateUserSettings as updateUserSettingsApi,
  saveLastClientId as saveLastClientIdApi,
  checkIfUserEmailExists as checkIfUserEmailExistsApi,
  checkIfUserPwcEmailSetup as checkIfUserPwcEmailSetupApi,
  checkIfUserEmailIsUnderDomain as checkIfUserEmailIsUnderDomainApi,
  checkIfUserSSOExists as checkIfUserSSOExistsApi,
  fetchRoleAssignments as fetchRoleAssignmentsApi,
  fetchUserPrivileges as fetchUserPrivilegesApi,
} from '../api';
import getModalActions from '../../shared/modals/getModalActions';

import {
  FETCH_USERS,
  FETCH_USERS_SUCCESS,
  FETCH_USERS_FAILURE,
  SHOW_USER_SETTINGS_FORM_FOR_EDIT,
  SHOW_USER_SETTINGS_FORM_FOR_ADD,
  HIDE_USER_SETTINGS_FORM_FOR_EDIT,
  HIDE_USER_SETTINGS_FORM_FOR_ADD,
  FETCH_INITIAL_DATA_FOR_USER_SETTINGS,
  FETCH_INITIAL_DATA_FOR_USER_SETTINGS_SUCCESS,
  FETCH_INITIAL_DATA_FOR_USER_SETTINGS_FORM,
  FETCH_INITIAL_DATA_FOR_USER_SETTINGS_FORM_SUCCESS,
  FETCH_INITIAL_DATA_FOR_USER_SETTINGS_FORM_FAILURE,
  FETCH_LOGGED_IN_USER_SETTINGS_SUCCESS,
  FETCH_LOGGED_IN_USER_SETTINGS_FAILURE,
  RESET_USER_TO_EDIT,
  SAVING_USER_SETTINGS,
  SAVING_USER_SETTINGS_SUCCESS,
  SAVING_USER_SETTINGS_FAILURE,
  DISABLE_READ_ONLY_MODE,
  SET_USER_MODIFIED,
  CHECK_IF_USER_SSO_EXISTS,
  CHECK_IF_USER_SSO_EXISTS_SUCCESS,
  CHECK_IF_USER_SSO_EXISTS_FAILURE,
  CHECK_IF_USER_EMAIL_EXISTS,
  CHECK_IF_USER_EMAIL_EXISTS_SUCCESS,
  CHECK_IF_USER_EMAIL_EXISTS_FAILURE,
  CHECK_IF_USER_PWC_EMAIL_SETUP,
  CHECK_IF_USER_PWC_EMAIL_SETUP_SUCCESS,
  CHECK_IF_USER_PWC_EMAIL_SETUP_FAILURE,
  CHECK_IF_USER_EMAIL_IS_UNDER_DOMAIN,
  CHECK_IF_USER_EMAIL_IS_UNDER_DOMAIN_SUCCESS,
  CHECK_IF_USER_EMAIL_IS_UNDER_DOMAIN_FAILURE,
  FETCH_ROLE_ASSIGNMENTS,
  FETCH_ROLE_ASSIGNMENTS_SUCCESS,
  FETCH_EXPIRATION_DATE_PRIVILEGE,
  FETCH_EXPIRATION_DATE_PRIVILEGE_SUCCESS,
  FETCH_EXPIRATION_DATE_PRIVILEGE_FAILURE,
} from './types';

import agGridConfig from '../../agGridConfig';
import { ALL_ASSIGNMENTS_CODE, ConfirmDialogModal, SetLastClientIdModal } from '../constants';
import { getAssignmentKey } from '../utils';

export const fetchUsers = () => async dispatch => {
  try {
    dispatch({
      type: FETCH_USERS,
    });
    const users = await fetchUsersApi();

    return dispatch({
      type: FETCH_USERS_SUCCESS,
      users,
    });
  } catch (error) {
    dispatch({
      type: FETCH_USERS_FAILURE,
    });
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchInitialDataForUserSettingsPage = (
  isFetchingInitialDataForUserSettings,
  currentPeriod
) => async dispatch => {
  await dispatch(fetchUsers());

  if (isFetchingInitialDataForUserSettings) {
    return;
  }

  dispatch({
    type: FETCH_INITIAL_DATA_FOR_USER_SETTINGS,
  });

  const userSettingsFormDataResponse = await fetchUserSettingsFormDataApi(currentPeriod.name);

  return dispatch({
    type: FETCH_INITIAL_DATA_FOR_USER_SETTINGS_SUCCESS,
    userSettingsFormData: userSettingsFormDataResponse,
  });
};

export const resetUserToEdit = () => ({
  type: RESET_USER_TO_EDIT,
});

export const hideUserSettingsFormForEdit = () => dispatch => {
  dispatch(resetUserToEdit());
  return dispatch({
    type: HIDE_USER_SETTINGS_FORM_FOR_EDIT,
  });
};

export const hideUserSettingsFormForAdd = () => dispatch => {
  dispatch(resetUserToEdit());
  return dispatch({
    type: HIDE_USER_SETTINGS_FORM_FOR_ADD,
  });
};

export const showUserSettingsFormForEdit = (userToEditSso, readOnly = false) => dispatch => {
  dispatch(hideUserSettingsFormForEdit());
  return dispatch({
    type: SHOW_USER_SETTINGS_FORM_FOR_EDIT,
    userToEditSso,
    readOnly,
  });
};

export const showUserSettingsFormForAdd = () => dispatch => {
  dispatch(hideUserSettingsFormForAdd());
  return dispatch({
    type: SHOW_USER_SETTINGS_FORM_FOR_ADD,
  });
};

export const fetchRoleAssignments = ({ roleId }) => async dispatch => {
  dispatch({ type: FETCH_ROLE_ASSIGNMENTS });

  try {
    const { roleAssignments } = await fetchRoleAssignmentsApi({ roleId });
    const roleAssignmentsMaps = _keyBy(roleAssignments.map(assignment => ({
      ...assignment,
      assignmentCode: assignment.assignmentCode === 'ALL'
        ? ALL_ASSIGNMENTS_CODE
        : assignment.assignmentCode,
      privileges: assignment.privileges.map(({ privilegeCode }) => privilegeCode),
      privilegesMap: _keyBy(assignment.privileges, 'privilegeCode'),
    })), getAssignmentKey);

    dispatch({ type: FETCH_ROLE_ASSIGNMENTS_SUCCESS, roleAssignmentsMaps, roleAssignments, roleId });

    return {
      roleAssignments,
      roleAssignmentsMaps,
    };
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};


export const fetchInitialDataForUserSettingsForm = ({
  openForEdit,
  userToEditSso,
}) => async dispatch => {
  try {
    dispatch({
      type: FETCH_INITIAL_DATA_FOR_USER_SETTINGS_FORM,
    });

    const userToEditResponse = openForEdit
      ? await fetchUserApi(userToEditSso)
      : null;

    if (openForEdit) {
      await userToEditResponse.user.clients.map(({ roleId }) => dispatch(fetchRoleAssignments({ roleId })));
    }

    return dispatch({
      type: FETCH_INITIAL_DATA_FOR_USER_SETTINGS_FORM_SUCCESS,
      userToEdit: openForEdit ? userToEditResponse.user : null,
      userToEditPrivileges: openForEdit ? userToEditResponse.privileges : [],
    });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch({
        type: FETCH_INITIAL_DATA_FOR_USER_SETTINGS_FORM_FAILURE,
      });
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchLoggedInUserSettings = ({
  sso,
}) => async dispatch => {
  try {
    const response = await fetchUserApi(sso);

    return dispatch({
      type: FETCH_LOGGED_IN_USER_SETTINGS_SUCCESS,
      userSettings: response.user,
    });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch({
        type: FETCH_LOGGED_IN_USER_SETTINGS_FAILURE,
      });
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const saveUserSettings = ({
  userSettingsData,
  isEditMode,
  usersGridApi,
}, callback) => async dispatch => {
  dispatch({
    type: SAVING_USER_SETTINGS,
  });

  try {
    if (isEditMode) {
      await updateUserSettingsApi({
        userSettingsData,
      });

      dispatch(successNotification('User Settings updated'));
    } else {
      await addUserSettingsApi({
        userSettingsData,
      });

      dispatch(successNotification('User Settings added'));
    }
    dispatch({
      type: SAVING_USER_SETTINGS_SUCCESS,
    });

    await dispatch(fetchUsers());

    if (isEditMode) {
      dispatch(hideUserSettingsFormForEdit());
    } else {
      dispatch(hideUserSettingsFormForAdd());
    }

    if (callback) {
      callback();
    }

    dispatch(selectRow({
      name: agGridConfig.adminUsersAndPrivileges.name,
      fields: agGridConfig.adminUsersAndPrivileges.fields,
      data: userSettingsData,
    }));

    // invoke ensureNodeVisible manually - the grid won't call it, because the grid's row itself is not updated
    await usersGridApi?.ensureNodeVisible(node =>
      node?.data?.sso === userSettingsData.sso && node?.data?.email === userSettingsData.email
    , 'top');
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      dispatch({
        type: SAVING_USER_SETTINGS_FAILURE,
      });
      return;
    }
    throw error;
  }
};

export const saveLastClientId = ({ sso, lastClientId }) =>
  async dispatch => {
    try {
      await saveLastClientIdApi({ sso, clientId: lastClientId });
      dispatch(successNotification('User last client id saved successfully'));
    } catch (error) {
      if (error.isFetchingError) {
        dispatch(fetchingError());
        dispatch(errorNotification(error.message));
        return;
      }
      throw error;
    }
  };

export const disableReadOnlyMode = () => ({
  type: DISABLE_READ_ONLY_MODE,
});

export const {
  showAction: showConfirmDialogModal,
  hideAction: hideConfirmDialogModal,
} = getModalActions(ConfirmDialogModal);

export const {
  showAction: showSetLastClientIdModal,
  hideAction: hideSetLastClientIdModal,
} = getModalActions(SetLastClientIdModal);

export const setUserModified = isUserModified => ({
  type: SET_USER_MODIFIED,
  isUserModified,
});

export const checkIfUserEmailExists = ({ email }) => async dispatch => {
  try {
    dispatch({
      type: CHECK_IF_USER_EMAIL_EXISTS,
    });
    const { isEmailUnique } = await checkIfUserEmailExistsApi({ email });
    dispatch({
      type: CHECK_IF_USER_EMAIL_EXISTS_SUCCESS,
      isEmailUnique,
    });
  } catch (error) {
    dispatch({
      type: CHECK_IF_USER_EMAIL_EXISTS_FAILURE,
    });
  }
};

export const checkIfUserPwcEmailSetup = ({ email }) => async dispatch => {
  try {
    dispatch({
      type: CHECK_IF_USER_PWC_EMAIL_SETUP,
    });
    const { isPwcEmailSetup } = await checkIfUserPwcEmailSetupApi({ email });
    dispatch({
      type: CHECK_IF_USER_PWC_EMAIL_SETUP_SUCCESS,
      isPwcEmailSetup,
    });
  } catch (error) {
    dispatch({
      type: CHECK_IF_USER_PWC_EMAIL_SETUP_FAILURE,
    });
  }
};

export const checkIfUserEmailIsUnderDomain = ({ email }) => async dispatch => {
  try {
    dispatch({
      type: CHECK_IF_USER_EMAIL_IS_UNDER_DOMAIN,
    });
    const { isUserEmailUnderDomain } = await checkIfUserEmailIsUnderDomainApi({ email });
    dispatch({
      type: CHECK_IF_USER_EMAIL_IS_UNDER_DOMAIN_SUCCESS,
      isUserEmailUnderDomain,
    });
  } catch (error) {
    dispatch({
      type: CHECK_IF_USER_EMAIL_IS_UNDER_DOMAIN_FAILURE,
    });
  }
};

export const checkIfUserSSOExists = ({ sso }) => async dispatch => {
  try {
    dispatch({
      type: CHECK_IF_USER_SSO_EXISTS,
    });
    const { isSSOUnique } = await checkIfUserSSOExistsApi({ sso });
    dispatch({
      type: CHECK_IF_USER_SSO_EXISTS_SUCCESS,
      isSSOUnique,
    });
  } catch (error) {
    dispatch({
      type: CHECK_IF_USER_SSO_EXISTS_FAILURE,
    });
  }
};

export const fetchExpirationDatePrivilege = () => async dispatch => {
  try {
    dispatch({ type: FETCH_EXPIRATION_DATE_PRIVILEGE });

    const isExpirationDatePrivilege = await fetchUserPrivilegesApi(assignablePermissionTypes.UserExpiration);
    dispatch({
      type: FETCH_EXPIRATION_DATE_PRIVILEGE_SUCCESS,
      isExpirationDatePrivilege,
    });
  } catch (error) {
    dispatch({ type: FETCH_EXPIRATION_DATE_PRIVILEGE_FAILURE });
  }
};
